import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, CalendarDays, CalendarRange, Infinity as InfinityIcon, Download, FileSpreadsheet, StickyNote, X, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  shipped:     { bg: 'bg-emerald-600',  ring: 'ring-emerald-600',  text: 'text-white',     label: 'Shipped' },
  completed:   { bg: 'bg-sky-500',      ring: 'ring-sky-500',      text: 'text-white',     label: 'Done' },
  pending:     { bg: 'bg-amber-300',    ring: 'ring-amber-400',    text: 'text-amber-900', label: 'Planned' },
  unplanned:   { bg: 'bg-slate-300',    ring: 'ring-slate-400',    text: 'text-slate-700', label: 'Unplanned' },
  overdue:     { bg: 'bg-rose-500',     ring: 'ring-rose-500',     text: 'text-white',     label: 'Backlog' },
};

const GRANULARITIES = [
  { id: 'day',   label: 'Daily',   icon: <Calendar className="w-3.5 h-3.5" /> },
  { id: 'week',  label: 'Weekly',  icon: <CalendarDays className="w-3.5 h-3.5" /> },
  { id: 'month', label: 'Monthly', icon: <CalendarRange className="w-3.5 h-3.5" /> },
  { id: 'all',   label: 'All',     icon: <InfinityIcon className="w-3.5 h-3.5" /> },
];

function NoteModal({ open, onClose, cell, projectId, projectName, granularityLabel, onSaved }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const userJson = typeof localStorage !== 'undefined' ? localStorage.getItem('user') : null;
  const user = userJson ? JSON.parse(userJson) : { name: '', email: '' };

  useEffect(() => {
    if (cell) setNote(cell.note || '');
  }, [cell]);

  useEffect(() => {
    if (!open || !cell || !projectId) {
      setHistory(null);
      return;
    }
    axios.get(`${API}/api/mdp-schedule/set-history/${projectId}/${cell.set_number}`)
      .then(r => setHistory(r.data))
      .catch(() => setHistory(null));
  }, [open, cell, projectId]);

  if (!open || !cell) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/mdp-schedule/set-note`, {
        project_id: projectId,
        set_number: cell.set_number,
        note,
        user_email: user.email,
        user_name: user.name,
      });
      toast.success(note.trim() ? 'Note saved' : 'Note cleared');
      onSaved();
      onClose();
    } catch (e) {
      toast.error('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const stStyle = STATUS_STYLE[cell.status] || STATUS_STYLE.pending;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="set-note-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-900 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{projectName}</div>
            <div className="text-lg font-bold text-white flex items-center gap-2">
              <StickyNote className="w-4 h-4" /> Set #{cell.set_number}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-white" data-testid="close-note-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center gap-3 text-xs">
            <span className={`px-2.5 py-1 rounded-full font-semibold ${stStyle.bg} ${stStyle.text}`}>{stStyle.label}</span>
            <span className="text-slate-500">Dock date: <span className="font-semibold text-slate-700">{cell.date}</span></span>
            <span className="text-slate-400">· {granularityLabel}</span>
          </div>

          {/* Set timeline */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Set Timeline</div>
            {!history ? (
              <div className="text-xs text-slate-400 py-3 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading history…</div>
            ) : (
              <div className="space-y-3">
                {/* Key dates row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                  <div className="border border-slate-200 rounded-lg px-2 py-1.5 bg-slate-50">
                    <div className="text-slate-400 uppercase tracking-wider">Dock Date</div>
                    <div className="font-semibold text-slate-800">{history.line_entry_date || '—'}</div>
                  </div>
                  <div className="border border-amber-200 rounded-lg px-2 py-1.5 bg-amber-50">
                    <div className="text-amber-700 uppercase tracking-wider">Expected Ship</div>
                    <div className="font-semibold text-slate-800">{history.expected_ship_date || '—'}</div>
                    <div className="text-[9px] text-slate-400">+{history.ship_days_offset} days</div>
                  </div>
                  <div className="border border-sky-200 rounded-lg px-2 py-1.5 bg-sky-50">
                    <div className="text-sky-700 uppercase tracking-wider">Done On</div>
                    <div className="font-semibold text-slate-800">{history.done_date || '—'}</div>
                  </div>
                  <div className="border border-emerald-200 rounded-lg px-2 py-1.5 bg-emerald-50">
                    <div className="text-emerald-700 uppercase tracking-wider">Shipped On</div>
                    <div className="font-semibold text-slate-800">{history.shipped_date || '—'}</div>
                  </div>
                </div>
                {/* Events list */}
                {history.events.length > 0 && (
                  <ol className="relative border-l-2 border-slate-200 ml-2 pl-4 space-y-2">
                    {history.events.map((ev, i) => {
                      const dotColor =
                        ev.kind === 'line' ? 'bg-slate-400'
                          : ev.kind === 'expected' ? 'bg-amber-400'
                            : ev.kind === 'done' ? 'bg-sky-500'
                              : ev.kind === 'shipped' ? 'bg-emerald-600'
                                : 'bg-slate-300';
                      return (
                        <li key={i} className="relative">
                          <span className={`absolute -left-[22px] top-1 w-3 h-3 rounded-full ${dotColor} ring-2 ring-white`}></span>
                          <div className="text-xs flex items-center justify-between gap-3">
                            <span className="font-semibold text-slate-700">{ev.label}</span>
                            <span className="font-mono text-slate-500">{ev.date}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Note</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Type a note for this set (e.g. 'Customer requested change', 'Pending material', etc.)"
              className="mt-2 w-full h-24 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 resize-none"
              data-testid="set-note-textarea" />
            {cell.note_user && (
              <p className="text-[11px] text-slate-400 mt-1">Last edited by <span className="font-semibold">{cell.note_user}</span> · {cell.note_updated?.split('T')[0]}</p>
            )}
          </div>
        </div>
        <div className="px-6 py-3 bg-slate-50 flex justify-end gap-2 border-t border-slate-200">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800" data-testid="cancel-note-btn">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-slate-800 text-white rounded-lg text-sm font-semibold hover:bg-slate-900 disabled:opacity-60 flex items-center gap-2" data-testid="save-note-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ open, onClose, onComplete }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const handleImport = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/api/mdp-schedule/import`, fd);
      setResult(data);
      toast.success(`Updated ${data.updated_projects} projects (${data.updated_sets} sets)`);
      onComplete && onComplete();
    } catch (e) {
      toast.error('Import failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="mdp-import-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-indigo-700 to-violet-700 flex items-center justify-between">
          <div className="text-lg font-bold text-white flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" /> Import MDP Schedule
          </div>
          <button onClick={onClose} className="text-indigo-100 hover:text-white" data-testid="close-import-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-slate-600">Upload the customer schedule Excel (MDP PO Report or Kiel Q3 raw). We will refresh <span className="font-semibold">dock dates</span> only — production and shipment data are untouched.</p>
          <label className="block">
            <input type="file" accept=".xlsx,.xls"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
              data-testid="mdp-import-file-input" />
          </label>
          {result && (
            <div className="text-xs space-y-1 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              {result.format && (
                <div className="text-slate-500 text-[10px] uppercase tracking-wider">Format detected: <span className="font-bold text-indigo-700">{result.format === 'kiel_raw' ? 'Kiel Q3 (raw)' : 'MDP PO Report'}</span></div>
              )}
              <div>✓ Updated projects: <span className="font-bold text-emerald-700">{result.updated_projects}</span></div>
              <div>✓ Updated set entries: <span className="font-bold text-emerald-700">{result.updated_sets}</span></div>
              {result.missing_count > 0 && (
                <div className="text-amber-700">⚠ {result.missing_count} SR not found in system: {result.missing_srs.join(', ')}</div>
              )}
            </div>
          )}
        </div>
        <div className="px-6 py-3 bg-slate-50 flex justify-end gap-2 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800">Close</button>
          <button onClick={handleImport} disabled={!file || busy}
            className="px-5 py-2 bg-indigo-700 text-white rounded-lg text-sm font-semibold hover:bg-indigo-800 disabled:opacity-60 flex items-center gap-2"
            data-testid="run-import-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Run Import
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MdpScheduleGrid({ category }) {
  const [granularity, setGranularity] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openNote, setOpenNote] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const scrollContainerRef = useRef(null);

  const fetchTimeline = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/mdp-schedule/timeline`, { params: { category, granularity } })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load timeline'))
      .finally(() => setLoading(false));
  }, [category, granularity]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  // Auto-scroll horizontally to current bucket once data is rendered.
  // We delay one tick so the table cells are actually mounted before reading
  // their layout positions.
  useEffect(() => {
    if (!data) return;
    const cur = data.current_bucket;
    if (!cur) return;
    const t = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const el = container.querySelector(`th[data-bucket-key="${cur}"]`);
      if (!el) return;
      // Keep the 3 sticky left columns (~350px) in view by offsetting left.
      const targetLeft = el.offsetLeft - 360;
      container.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
    }, 120);
    return () => clearTimeout(t);
  }, [data]);

  const today = data?.today;

  const handleExport = async (includeOther) => {
    setExportOpen(false);
    try {
      const res = await axios.get(`${API}/api/mdp-schedule/export`, {
        params: { category, granularity, include_other_category: includeOther ? 'true' : 'false' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `MDP_Schedule_${category.replace(/\s/g, '')}_${granularity}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (e) {
      toast.error('Export failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const columnTotals = useMemo(() => {
    const totals = {};
    if (!data) return totals;
    data.columns.forEach(c => { totals[c.key] = 0; });
    data.projects.forEach(p => {
      Object.entries(p.cells).forEach(([k, arr]) => { totals[k] = (totals[k] || 0) + arr.length; });
    });
    return totals;
  }, [data]);

  const totals = useMemo(() => {
    if (!data) return { projects: 0, sets: 0 };
    let setCount = 0;
    data.projects.forEach(p => Object.values(p.cells).forEach(arr => { setCount += arr.length; }));
    return { projects: data.projects.length, sets: setCount };
  }, [data]);

  const otherCategory = category === 'City Bus' ? 'Coach' : 'City Bus';

  return (
    <div className="space-y-3" data-testid="mdp-schedule-grid">
      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Master Schedule</div>
          <h3 className="text-lg font-bold text-slate-800">{category} — MDP Timeline</h3>
          <p className="text-xs text-slate-500 mt-0.5">{totals.projects} projects · {totals.sets} scheduled sets</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="granularity-tabs">
            {GRANULARITIES.map(g => (
              <button key={g.id} onClick={() => setGranularity(g.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${granularity === g.id ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
                data-testid={`granularity-${g.id}`}>
                {g.icon}
                {g.label}
              </button>
            ))}
          </div>
          <button onClick={() => setExportOpen(true)}
            className="flex items-center gap-2 px-3.5 py-2 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 shadow-sm"
            data-testid="export-schedule-btn">
            <Download className="w-3.5 h-3.5" /> Export Excel
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
        <span className="font-semibold text-slate-700">Legend:</span>
        {Object.entries(STATUS_STYLE).map(([k, s]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded ${s.bg}`}></span>
            {s.label}
          </span>
        ))}
        <span className="ml-auto text-slate-400">Click any cell to add a note</span>
      </div>

      {/* Grid */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-20 flex items-center justify-center text-slate-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading schedule…
          </div>
        ) : !data || data.columns.length === 0 ? (
          <div className="py-16 px-6 text-center">
            <p className="text-sm text-slate-500">No scheduled sets yet for <span className="font-semibold">{category}</span>.</p>
            <p className="text-xs text-slate-400 mt-1">Use <span className="font-semibold">MDP Import</span> on the Planning Projects page to upload the customer schedule.</p>
          </div>
        ) : (
          <div className="overflow-auto max-h-[680px]" ref={scrollContainerRef}>
            <table className="text-xs border-collapse" data-testid="schedule-table">
              <thead className="sticky top-0 z-20 bg-slate-800 text-white">
                <tr>
                  <th className="sticky left-0 z-30 bg-slate-800 px-3 py-2 text-left font-semibold border-r-2 border-slate-500" style={{ minWidth: 200 }}>Project</th>
                  <th className="sticky z-30 bg-slate-800 px-3 py-2 text-left font-semibold border-r border-slate-700" style={{ left: 200, minWidth: 90 }}>SR</th>
                  <th className="sticky z-30 bg-slate-800 px-3 py-2 text-center font-semibold border-r-2 border-slate-500" style={{ left: 290, minWidth: 60 }}>Ord</th>
                  {data.columns.map(col => {
                    const isCurrent = col.key === data.current_bucket;
                    return (
                      <th key={col.key} data-bucket-key={col.key}
                        className={`px-2 py-2 text-center font-semibold whitespace-nowrap border-l-2 ${isCurrent ? 'bg-yellow-400 text-slate-900 border-l-yellow-500 border-r-2 border-r-yellow-500 ring-2 ring-yellow-500 ring-inset' : 'border-slate-600'}`}
                        style={{ minWidth: granularity === 'day' ? 70 : 140 }}>
                        <div className="flex items-center justify-center gap-1.5">
                          {isCurrent && <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></span>}
                          <span>{col.label}</span>
                        </div>
                        <div className={`text-[10px] font-normal mt-0.5 ${isCurrent ? 'text-slate-800 font-bold' : 'text-slate-300'}`}>
                          {columnTotals[col.key] || 0} set{columnTotals[col.key] === 1 ? '' : 's'}
                          {isCurrent && ' · TODAY'}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.projects.map((p, ri) => {
                  const rowBg = ri % 2 === 0 ? 'bg-white' : 'bg-slate-50';
                  return (
                  <tr key={p.id} className={`${rowBg} border-b border-slate-200`}>
                    <td className={`sticky left-0 z-10 ${rowBg} px-3 py-2 font-semibold text-slate-800 border-r-2 border-slate-300 border-b border-slate-200`} style={{ minWidth: 200 }}>
                      <div className="truncate" title={p.name}>{p.name}</div>
                      {p.customer_name && <div className="text-[10px] text-slate-400 truncate">{p.customer_name}</div>}
                    </td>
                    <td className={`sticky z-10 ${rowBg} px-3 py-2 text-slate-500 font-mono border-r border-slate-200 border-b border-slate-200`} style={{ left: 200, minWidth: 90 }}>{p.sr_number}</td>
                    <td className={`sticky z-10 ${rowBg} px-3 py-2 text-center font-semibold text-slate-700 border-r-2 border-slate-300 border-b border-slate-200`} style={{ left: 290, minWidth: 60 }}>{p.ordered}</td>
                    {data.columns.map(col => {
                      const cells = p.cells[col.key] || [];
                      const isCurrent = col.key === data.current_bucket;
                      const currentBg = isCurrent ? 'bg-yellow-100' : '';
                      if (cells.length === 0) {
                        return <td key={col.key} className={`border-l-2 border-slate-200 border-b border-slate-200 ${currentBg}`}></td>;
                      }
                      const sets = [...new Set(cells.map(c => c.set_number))].sort((a, b) => a - b);
                      return (
                        <td key={col.key} className={`p-1 border-l-2 border-slate-200 border-b border-slate-200 align-middle ${currentBg}`}>
                          <div className="flex flex-wrap gap-1 justify-center">
                            {cells.sort((a, b) => a.set_number - b.set_number).map(c => {
                              let cstatus = c.status;
                              // Overdue (Backlog) override: dock date passed and not yet done/shipped
                              if ((cstatus === 'pending' || cstatus === 'unplanned') && c.date < today) {
                                cstatus = 'overdue';
                              }
                              if (cstatus === 'in_progress') cstatus = 'pending';  // legacy fallback
                              const cs = STATUS_STYLE[cstatus] || STATUS_STYLE.unplanned;
                              return (
                                <button key={`${c.set_number}-${c.date}`}
                                  onClick={() => setOpenNote({ cell: c, projectId: p.id, projectName: p.name, granularityLabel: col.label })}
                                  className={`relative px-2 py-1 rounded text-[11px] font-bold leading-none ${cs.bg} ${cs.text} hover:scale-110 transition-transform shadow-sm`}
                                  title={`Set ${c.set_number} · ${cs.label} · ${c.date}${c.note ? '\n📝 ' + c.note : ''}`}
                                  data-testid={`cell-${p.sr_number}-set${c.set_number}`}>
                                  {c.set_number}
                                  {c.note && <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full ring-2 ring-white"></span>}
                                </button>
                              );
                            })}
                          </div>
                          <div className="text-[10px] text-center text-slate-400 mt-0.5">{sets.length}</div>
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 z-20 bg-slate-100 border-t-2 border-slate-300">
                <tr>
                  <td className="sticky left-0 z-30 bg-slate-100 px-3 py-2 font-bold text-slate-700 border-r-2 border-slate-300" style={{ minWidth: 200 }}>TOTAL</td>
                  <td className="sticky z-30 bg-slate-100 border-r border-slate-200" style={{ left: 200, minWidth: 90 }}></td>
                  <td className="sticky z-30 bg-slate-100 px-3 py-2 text-center font-bold text-slate-700 border-r-2 border-slate-300" style={{ left: 290, minWidth: 60 }}>
                    {data.projects.reduce((s, p) => s + p.ordered, 0)}
                  </td>
                  {data.columns.map(col => (
                    <td key={col.key} className="px-2 py-2 text-center font-bold text-slate-700 border-l-2 border-slate-200">
                      {columnTotals[col.key] || 0}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Note modal */}
      <NoteModal
        open={!!openNote} onClose={() => setOpenNote(null)}
        cell={openNote?.cell} projectId={openNote?.projectId} projectName={openNote?.projectName}
        granularityLabel={openNote?.granularityLabel || ''}
        onSaved={fetchTimeline}
      />

      {/* Export confirm modal */}
      {exportOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="export-confirm-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600">
              <div className="text-lg font-bold text-white">Export Schedule</div>
            </div>
            <div className="px-6 py-5 text-sm text-slate-700">
              You're exporting the <span className="font-bold">{category}</span> schedule.
              Include <span className="font-bold">{otherCategory}</span> as an additional sheet?
            </div>
            <div className="px-6 py-3 bg-slate-50 flex justify-end gap-2 border-t border-slate-200">
              <button onClick={() => handleExport(false)} className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg" data-testid="export-only-current">
                Only {category}
              </button>
              <button onClick={() => handleExport(true)} className="px-5 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700" data-testid="export-both">
                Include {otherCategory}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { ImportModal as MdpImportModal };
